<script setup>
import { computed, ref } from "vue"
import { useRouter } from "vue-router"
import { getPayoutRangeString } from "@/utils/payoutRange"
import ExpandableCountriesFromPayouts from "@/components/common/ExpandableCountriesFromPayouts.vue"
import { useCampaignImages } from "@/composables/useCampaignImages.js"

const isDragging = ref(false)
const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
})

const { campaignImages, logoImage, getImageUrl } = useCampaignImages()


campaignImages.value = props.campaign.images

const computedPayouts = computed(() => {
  return props.campaign.status !== "subscribed" &&
    props.campaign.payoutsGlobal?.length > 0
    ? props.campaign.payoutsGlobal
    : props.campaign.payouts
})

const filteredPayouts = computed(() => {
  if (!computedPayouts.value) {
    return []
  }
  return computedPayouts.value.filter(payout => payout.value > 0)
})

const payoutSummary = computed(() => getPayoutRangeString(filteredPayouts.value))

// Drag detection handlers using pointer events
let pointerDownTime = 0
let pointerMoved = false
let dragStartPos = { x: 0, y: 0 }

const handlePointerDown = (event) => {
  pointerDownTime = Date.now()
  pointerMoved = false
  dragStartPos = { x: event.clientX, y: event.clientY }
}

const handlePointerMove = (event) => {
  if (pointerDownTime > 0) {
    const deltaX = Math.abs(event.clientX - dragStartPos.x)
    const deltaY = Math.abs(event.clientY - dragStartPos.y)
    
    // If pointer moved more than 3px in any direction, consider it a drag
    if (deltaX > 3 || deltaY > 3) {
      pointerMoved = true
    }
  }
}

const handlePointerUp = () => {
  const timeDiff = Date.now() - pointerDownTime
  
  // Reset for next interaction
  pointerDownTime = 0
  
  // If pointer moved or interaction was long (drag), prevent click
  if (pointerMoved || timeDiff > 200) {
    isDragging.value = true
    
    // Reset isDragging after a short delay to allow next click
    setTimeout(() => {
      isDragging.value = false
    }, 100)
  } else {
    isDragging.value = false
  }
}

const router = useRouter()

const handleNavigate = (event) => {
  // If we detected dragging, don't navigate
  if (isDragging.value) {
    event.preventDefault()
    event.stopPropagation()
    return false
  }
  
  // Navigate programmatically
  router.push({
    name: 'Campaign',
    params: { id: props.campaign.id }
  })
}
</script>

<template>
  <div
    v-if="campaign"
    class="group h-[16.85rem] border-zinc-300 border dark:border-transparent block w-full overflow-hidden relative feature-card flex flex-col bg-base-300  rounded-md px-4 py-4 overflow-hidden cursor-pointer  transition-colors"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @click="handleNavigate"
  >
    <div
      class="z-50 mb-2 flex justify-center items-center"
      style="max-height: 6rem; height: 6rem"
    >
      <div
        class="block w-full"
      >
        <figure
          class="flex justify-center items-center w-full group-hover:scale-105 transition-transform duration-300"
          style="height: 6rem"
        >
    <img
          v-if="campaignImages?.length > 0"
            :src="
              campaignImages[0].conversions.filter(
                image => image.conversion === '300x200'
              )[0].storageUrl
            "
            class="object-cover w-full h-48 rounded-md block border border-base-100 dark:border-transparent"
            draggable="false"
          />
          <img
            v-if="logoImage"
            :src="getImageUrl(logoImage, 'md')"
            :alt="`${campaign.name} logo`"
            class="h-full w-auto"
            draggable="false"
          />
        </figure>
      </div>
    </div>

    <div class="z-50 flex flex-col justify-between h-full text-left">
      <h2 class="font-semibold pt-2 leading-5 line-clamp-2 h-12 text-sm">
        {{ campaign.name }}
      </h2>
      <div class="flex">
        <Tag v-if="campaign.verticalTags" v-for="tag in campaign.verticalTags" :key="tag.id">{{ tag.name }}</Tag>
      </div>
      <div class="flex flex-col mt-2.5 gap-[0.2rem] text-base-content/80">
        <div
          v-if="!computedPayouts"
          class="skeleton h-3 mb-1 w-full opacity-40"
        ></div>

        <div v-else class="text-xs flex items-center">
          <v-icon
            name="md-attachmoney-round"
            size="6"
            class="mapMarker"
          ></v-icon>

          <div
            :class="[
              'text-xs font-normal',
              // 'text-xxs py-0.5 px-1.5 border border-amber-400 text-amber-400 rounded-md'
            ]"
          >
            {{ payoutSummary ?? "Variable CPA" }}
          </div>
        </div>
        <div
          v-if="!computedPayouts"
          class="skeleton h-3 mb-1 w-full opacity-40"
        ></div>
        <div v-else class="flex text-xs font-normal items-center">
          <v-icon name="md-locationon-outlined" class="mapMarker"></v-icon>

          <ExpandableCountriesFromPayouts
            :payouts="computedPayouts"
            :useFlags="true"
            :showCount="4"
          ></ExpandableCountriesFromPayouts>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.mapMarker {
  @apply text-base-content/20 inline mr-1.5;
}

figure > img {
  @apply h-full w-full;
}
.feature-card::after {
  /* content: "";
  position: absolute;
  top: 0;
  left: 10%;
  width: 80%;
  height: 68%;
  background-image: url("@/assets/images/dashboard/feature-card-blob.webp");
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position: center top;
  background-size: cover;
  opacity: 0.2; */
}

@media (min-width: 768px) {
  .dropdown:hover .dropdown-content {
    display: grid;
  }
}
</style>
